import React, { useCallback } from 'react';
import { Dialog, ErrorMessage } from '@koopajs/mui';
import { Typography, Button, Box, Container, Stack } from '@mui/material';
import { useHistory } from 'react-router-dom';
import { useLocale, useResourceDelete } from '@koopajs/react';
import { useConfirmDelete } from 'components/hooks/useConfirmDelete';
import { IUploadedMeeting } from 'types';
import { useAppSnackbar } from 'components/hooks/useAppSnackbar';
import { Operations } from '@koopajs/app';
import { useDispatch } from 'react-redux';
import { useWorkspaceShow, useSearchParams } from '@koopajs/react';

interface IDeleteUploadedMeeting {
  uploadedMeeting: IUploadedMeeting;
  isOpen: boolean;
  onClose: () => void;
}

export const DeleteUploadedMeeting: React.FC<IDeleteUploadedMeeting> = (props) => {
  const { uploadedMeeting, isOpen, onClose } = props;

  const { t } = useLocale();
  const history = useHistory();
  const keyPrefix = 'Modals.ModalDeleteUploadedMeeting';

  const { triggerSnackbar } = useAppSnackbar();

  const dispatch = useDispatch();
  const resourcesFetchOperation = Operations.Resources.fetch(dispatch);
  const { workspace } = useWorkspaceShow();
  const search = useSearchParams();

  const {
    deleteResource: deleteUploadedMeeting,
    errorMessage,
    isProcessing
  } = useResourceDelete({
    path: '/uploaded-meetings',
    id: uploadedMeeting?.id as string
  });

  const { hasUserTypedDeleteConfirmation, renderConfirmDeleteField, resetConfirmDeleteField } =
    useConfirmDelete({
      i18nKeyPrefix: keyPrefix + '.ConfirmDeleteField'
    });

  const handleCloseModal = useCallback(() => {
    onClose();
    resetConfirmDeleteField();
  }, []);

  const handleDeleteUploadedMeeting = useCallback(async () => {
    const isSuccess = await deleteUploadedMeeting();

    if (isSuccess) {
      await new Promise((resolve) => setTimeout(resolve, 1200));

      await resourcesFetchOperation(workspace?.id || '', `/past-meetings`, search.searchParams);
      handleCloseModal();

      history.push({ pathname: '/past-meetings', search: history.location?.search });

      triggerSnackbar({
        snackbarText: t(keyPrefix + '.snackbarDeleteSuccess'),
        variant: 'success'
      });
    } else {
      triggerSnackbar({
        snackbarText: t(keyPrefix + '.snackbarDeleteFail'),
        variant: 'error'
      });
    }
  }, [deleteUploadedMeeting, uploadedMeeting?.id]);

  return (
    <Dialog.View
      isOpen={isOpen}
      onClose={handleCloseModal}
      forcedFullScreen={false}
      dialogProps={{ PaperProps: { 'data-cy': 'delete-uploaded-meeting-modal' } }}
      maxWidth="md"
    >
      <Container
        sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 3 }}
      >
        <Typography variant="h6">{t(keyPrefix + '.title')}</Typography>

        <ErrorMessage error={errorMessage} />

        <Typography sx={{ whiteSpace: 'pre-line' }}>{t(keyPrefix + '.description')}</Typography>

        {renderConfirmDeleteField()}

        <Stack gap={2} flexDirection="row" justifyContent="center" flexWrap="wrap">
          <Button sx={{ px: 7, mx: 1 }} variant="outlined" onClick={handleCloseModal}>
            {t('common:labelCancel')}
          </Button>
          <Button
            data-cy="delete-uploaded-meeting-modal_delete-button"
            color="error"
            sx={{ px: 6.5, mx: 1 }}
            variant="contained"
            onClick={handleDeleteUploadedMeeting}
            disabled={!hasUserTypedDeleteConfirmation}
          >
            {t(keyPrefix + '.labelDelete')}
          </Button>
        </Stack>
      </Container>
    </Dialog.View>
  );
};
